import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import {
  FEATURE_SECTIONS,
  FEATURE_PILLARS,
  FEATURE_FAQ,
} from "../data/featuresContent";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const FeaturesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="features-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              Features
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Tools that feel like{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                turning a page
              </span>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed mb-10"
              style={{ color: "var(--text-soft)" }}
            >
              PagePeers gives you everything a modern reader needs — shelf
              management, gentle progress tracking, community discovery, and a
              interface quiet enough to disappear while you read.
            </p>
          </motion.div>

          {/* Jump nav */}
          <motion.nav
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-2 mb-20 md:mb-28"
            aria-label="Feature sections"
          >
            {FEATURE_SECTIONS.map((f) => (
              <a
                key={f.id}
                href={`#${f.id}`}
                className="text-xs md:text-sm px-4 py-2 rounded-full border transition-colors hover:scale-[1.02]"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text-soft)",
                  background: "var(--surface)",
                }}
              >
                {f.title}
              </a>
            ))}
          </motion.nav>

          {/* Pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-24 md:mb-32">
            {FEATURE_PILLARS.map((pillar, i) => (
              <FadeIn key={pillar.title} delay={i * 0.08}>
                <div
                  className="rounded-3xl p-8 h-full text-center md:text-left"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <h2
                    className="font-serif text-xl font-medium mb-3"
                    style={{ color: "var(--text)" }}
                  >
                    {pillar.title}
                  </h2>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    {pillar.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Deep-dive sections */}
        <div className="space-y-24 md:space-y-36">
          {FEATURE_SECTIONS.map((feature, i) => {
            const Icon = feature.icon;
            const reversed = i % 2 === 1;

            return (
              <section
                key={feature.id}
                id={feature.id}
                data-testid={`feature-detail-${feature.id}`}
                className="scroll-mt-32"
                style={{
                  background: i % 2 === 0 ? "var(--bg-alt)" : "var(--bg)",
                }}
              >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
                  <div
                    className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                      reversed ? "lg:[direction:rtl]" : ""
                    }`}
                  >
                    <FadeIn className={reversed ? "lg:[direction:ltr]" : ""}>
                      <motion.div
                        className="relative rounded-3xl p-12 md:p-16 overflow-hidden cozy-card"
                        style={{
                          background:
                            "linear-gradient(145deg, var(--brand-tint) 0%, var(--surface) 55%)",
                          minHeight: "280px",
                        }}
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.5, ease }}
                      >
                        <motion.div
                          className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full blur-3xl opacity-50"
                          style={{ background: "var(--accent-soft)" }}
                          animate={{
                            scale: [1, 1.1, 1],
                            opacity: [0.3, 0.5, 0.3],
                          }}
                          transition={{
                            duration: 5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                        <div
                          className="relative w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                          style={{ background: "var(--surface)" }}
                        >
                          <Icon
                            className="w-9 h-9"
                            style={{ color: "var(--brand)" }}
                            strokeWidth={1.8}
                          />
                        </div>
                        <p
                          className="relative font-serif text-2xl md:text-3xl italic leading-snug max-w-sm"
                          style={{ color: "var(--text)" }}
                        >
                          {feature.tagline}
                        </p>
                      </motion.div>
                    </FadeIn>

                    <div className={reversed ? "lg:[direction:ltr]" : ""}>
                      <FadeIn delay={0.1}>
                        <span
                          className="text-[11px] uppercase tracking-[0.2em] font-semibold"
                          style={{ color: "var(--brand)" }}
                        >
                          Feature {String(i + 1).padStart(2, "0")}
                        </span>
                        <h2
                          className="font-serif text-3xl md:text-4xl font-medium tracking-tight mt-3 mb-6"
                          style={{ color: "var(--text)" }}
                        >
                          {feature.title}
                        </h2>
                        {feature.paragraphs.map((p, j) => (
                          <p
                            key={j}
                            className="text-base md:text-lg leading-[1.85] mb-5"
                            style={{ color: "var(--text-soft)" }}
                          >
                            {p}
                          </p>
                        ))}
                        <ul className="space-y-3 mb-8">
                          {feature.bullets.map((bullet) => (
                            <li
                              key={bullet}
                              className="flex items-start gap-3 text-sm md:text-base"
                              style={{ color: "var(--text-soft)" }}
                            >
                              <Check
                                className="w-5 h-5 shrink-0 mt-0.5"
                                style={{ color: "var(--brand)" }}
                              />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                        <p
                          className="text-sm italic px-4 py-3 rounded-2xl inline-block"
                          style={{
                            background: "var(--brand-tint)",
                            color: "var(--text)",
                          }}
                        >
                          {feature.highlight}
                        </p>
                      </FadeIn>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <FadeIn>
            <h2
              className="font-serif text-3xl md:text-4xl font-medium text-center mb-12"
              style={{ color: "var(--text)" }}
            >
              Common questions
            </h2>
          </FadeIn>
          <div className="space-y-4">
            {FEATURE_FAQ.map((item, i) => (
              <FadeIn key={item.q} delay={i * 0.06}>
                <div
                  className="rounded-2xl p-6 md:p-8 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <h3
                    className="font-serif text-lg md:text-xl font-medium mb-3"
                    style={{ color: "var(--text)" }}
                  >
                    {item.q}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    {item.a}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="rounded-3xl p-10 md:p-14 text-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <h2
              className="font-serif text-3xl md:text-4xl font-medium mb-4"
              style={{ color: "var(--text)" }}
            >
              Try every feature free
            </h2>
            <p
              className="text-base mb-8 max-w-lg mx-auto"
              style={{ color: "var(--text-soft)" }}
            >
              Create your shelf, browse community collections, and see how calm
              reading software can feel.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--brand)",
                  color: "var(--on-brand)",
                  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                }}
              >
                Join the Club
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                  background: "var(--surface)",
                }}
              >
                See how it works
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default FeaturesPage;
